/**
 *
 *  User Profile Page
 *  Payments Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useState, useRef } from "react";
import Modal from "react-modal";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useAppState } from "../../../state";

// Components
import Button from "../../Button/Button";
import AddCardModal from "../../AddCardModal/AddCardModal";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";

// Utils
import { modalStyles } from "../../../constants";

// Icons
import Visa from "./Visa";
import ApplePayIcon from "../../../icons/ApplePayPaymentMethodIcon";
import CreditCardIcon from "../../../icons/CreditCardIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";
import PlusIcon from "../../../icons/PlusIcon";
import CloseIcon from "../../../icons/CloseIcon";
import CheckOIcon from "../../../icons/CheckOIcon";

// Images
import payPal from "../../../images/payment-method-paypal.png";
import payPal2x from "../../../images/payment-method-paypal@2x.png";
import payPal3x from "../../../images/payment-method-paypal@3x.png";

import applePay from "../../../images/payment-method-apple-pay.png";
import applePay2x from "../../../images/payment-method-apple-pay@2x.png";
import applePay3x from "../../../images/payment-method-apple-pay@3x.png";

import visa from "../../../images/payment-method-visa.png";
import visa2x from "../../../images/payment-method-visa@2x.png";
import visa3x from "../../../images/payment-method-visa@3x.png";

import mastercard from "../../../images/payment-method-mastercard.png";
import mastercard2x from "../../../images/payment-method-mastercard@2x.png";
import mastercard3x from "../../../images/payment-method-mastercard@3x.png";

import amex from "../../../images/payment-method-american-express.png";
import amex2x from "../../../images/payment-method-american-express@2x.png";
import amex3x from "../../../images/payment-method-american-express@3x.png";

export const PaymentMethodItem = ({
  id,
  className,
  disabled,
  active,
  type,
  label /*cardName*/,
  onChange,
}) => {
  const handleChange = useCallback(() => {
    onChange && onChange(id);
  }, [onChange]);

  return (
    <div
      className={`${className} tbk-rounded-2xl ${
        active
          ? "tbk-bg-basic-white tbk-shadow-trube"
          : "tbk-border tbk-border-solid tbk-border-blue-grey-light"
      } tbk-cursor-pointer`}
      onClick={handleChange}
    >
      <div className="tbk-p-3">
        <input
          type="radio"
          id={id}
          checked={active}
          disabled={disabled}
          onChange={handleChange}
        />
        <label
          className="tbk-mb-0 tbk-flex tbk-items-center tbk-pl-5"
          htmlFor={id}
        >
          <div className="tbk-mr-2 tbk-shrink-0">
            {type === "visa" ? (
              <Visa />
            ) : type === "mastercard" ? (
              <img
                className="tbk-w-3"
                src={mastercard}
                srcSet={`${mastercard} 1x, ${mastercard2x} 2x`}
                alt="Mastercard"
              />
            ) : type === "amex" ? (
              <img
                className="tbk-w-3"
                src={amex}
                srcSet={`${amex} 1x, ${amex2x} 2x`}
                alt="Mastercard"
              />
            ) : type === "ap" ? (
              <ApplePayIcon />
            ) : (
              <CreditCardIcon />
            )}
          </div>
          <div className="tbk-text-main tbk-grow tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap">
            {type === "ap" ? "Apple Pay" : label}
          </div>
          {active ? (
            <div className="tbk-shrink-0 tbk-text-green">
              <CheckNiceIcon />
            </div>
          ) : null}
        </label>
      </div>
    </div>
  );
};

export const Payments = () => {
  const { theme } = useTheme();
  const stripe = useStripe();
  const elements = useElements();

  const {
    user,
    paymentMethods,
    loadPaymentMethods,
    createSetupIntent,
    registerPaymentMethod,
    setDefaultPaymentMethod,
    areUserPaymentMethodsFetching,
    isClientSecretFetching,
  } = useAppState();

  const [isAddCardModalOpen, setAddCardModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");
  const [isCardSetupFetching, setIsCardSetupFetching] = useState(false);

  const infoModalResolveRef = useRef(null);
  const setDefaultPaymentMethodRef = useRef(setDefaultPaymentMethod);

  useEffect(() => {
    if (user && user.id) {
      // The Apple Pay JS API is available.
      loadPaymentMethods(
        user.id,
        window.ApplePaySession ? {} : { provider: "CARD" }
      );
    }
  }, [user]);

  useEffect(() => {
    setDefaultPaymentMethodRef.current = setDefaultPaymentMethod;
  }, [setDefaultPaymentMethod]);

  const closeAddCardModal = useCallback(() => {
    setAddCardModalOpen(false);
  }, []);

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  // TODO dry this
  const handleValidSubmitCardForm = useCallback(
    (cardElement) => {
      // const { userId, userFullName, isClientSecretFetching } = this.props;
      var params;

      if (!user || !user.id) {
        console.log(`The user with id ${user && user.id} could not be found.`);

        return;
      }

      if (!isClientSecretFetching && !isCardSetupFetching) {
        createSetupIntent(user.id).then((res) => {
          setIsCardSetupFetching(true);

          stripe
            .confirmCardSetup(res.data.clientSecret, {
              payment_method: {
                card: cardElement,
                billing_details: { name: `${user.firstName} ${user.lastName}` },
              },
            })
            .then((result) => {
              if (result.error) {
                // Display error.message in your UI.
                openInfoModal(result.error.message);
              } else {
                // The setup has succeeded. Display a success message.
                params = {
                  customerId: user.id,
                  paymentMethod: result.setupIntent.payment_method,
                  provider: "CARD",
                };

                registerPaymentMethod(params).then((res) => {
                  // Some dirty hack
                  setDefaultPaymentMethodRef.current(res.data.id, {
                    customerId: res.data.customerId,
                  });

                  closeAddCardModal();
                });
              }
            })
            .finally(() => {
              setIsCardSetupFetching(false);
            });
        });
      }
    },
    [
      user,
      stripe,
      isCardSetupFetching,
      isClientSecretFetching,
      registerPaymentMethod,
    ] // eslint-disable-line
  );

  const handleCardChange = useCallback((e) => {}, []);

  const handlePaymentMethodChange = useCallback(
    (id) => {
      const pm = paymentMethods.find((val) => val.id === id);

      if (!pm || !pm.main) {
        setDefaultPaymentMethod(id, {
          customerId: user.id,
        });
      }
    },
    [user, paymentMethods, setDefaultPaymentMethod]
  );

  return (
    <>
      <ScrollToTopOnMount />
      <div className="tbk-mt-4 lg:tbk-mt-5 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-items-start lg:tbk-gap-5">
        <div>
          <div className="tbk-text-h3-subtitle tbk-mb-3 tbk-text-primary">
            Your Payment Methods:
          </div>
          {paymentMethods && paymentMethods.length
            ? paymentMethods.map((val) => (
                <PaymentMethodItem
                  key={val.id}
                  id={val.id}
                  active={val.main}
                  className="tbk-mb-2"
                  type={val.provider === "APPLE" ? "ap" : val.brand}
                  label={`${
                    val.provider === "APPLE"
                      ? "Apple Pay"
                      : val.brand === "visa"
                      ? "Visa"
                      : val.brand === "mastercard"
                      ? "Mastercard"
                      : val.brand === "amex"
                      ? "American Express"
                      : "Card"
                  }${
                    val.provider !== "APPLE"
                      ? ` ending *${val.lastNumbers}`
                      : ""
                  }`}
                  onChange={handlePaymentMethodChange}
                />
              ))
            : null}
          <Button
            variant="secondary"
            className="tbk-w-full"
            startIcon={<PlusIcon className="tbk-text-coral" />}
            onClick={() => setAddCardModalOpen(true)}
          >
            <span className="tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap">
              Add New Payment Method
            </span>
          </Button>
        </div>
        <div className="tbk-px-2 tbk-pt-5 tbk-pb-5 md:tbk-px-7 md:tbk-pt-7 md:tbk-pb-7">
          <h2 className="tbk-text-h2-black tbk-mb-1 tbk-text-blue-grey">
            We Accept
          </h2>
          <p className="tbk-text-main tbk-mb-3 tbk-text-blue-grey">
            Multiple payments methods, including credit cards and popular online
            payment services.
          </p>
          <div className="tbk-flex tbk-items-center tbk-justify-between">
            <img
              className=""
              src={applePay}
              srcSet={`${applePay} 1x, ${applePay2x} 2x, ${applePay3x} 3x`}
              alt="Apple Pay"
              width="48"
              height="18"
            />
            <img
              className="tbk-hidden"
              src={payPal}
              srcSet={`${payPal} 1x, ${payPal2x} 2x, ${payPal3x} 3x`}
              alt="PayPal"
              width="78"
              height="14"
            />
            <img
              className=""
              src={visa}
              srcSet={`${visa} 1x, ${visa2x} 2x, ${visa3x} 3x`}
              alt="Visa"
              width="54"
              height="18"
            />
            <img
              className=""
              src={mastercard}
              srcSet={`${mastercard} 1x, ${mastercard2x} 2x, ${mastercard3x} 3x`}
              alt="Mastercard"
              width="44"
              height="32"
            />
            <img
              className=""
              src={amex}
              srcSet={`${amex} 1x, ${amex2x} 2x, ${amex3x} 3x`}
              alt="American Express"
              width="32"
              height="32"
            />
          </div>
        </div>
      </div>
      <AddCardModal
        theme={theme}
        elements={elements}
        isOpen={isAddCardModalOpen}
        onClose={closeAddCardModal}
        isFetching={
          areUserPaymentMethodsFetching ||
          isClientSecretFetching ||
          isCardSetupFetching
        }
        onCardChange={handleCardChange}
        // onSubmit={handleSubmitCardForm}
        onValidSubmit={handleValidSubmitCardForm}
      />
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={modalStyles.info}
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <CheckOIcon className="tbk-mb-3" />

          <h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-1 tbk-text-primary">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-3 tbk-text-center tbk-text-primary">
            {infoModalMsg}
          </p>
          <Button
            size="md"
            color="green"
            className="tb-w-100"
            onClick={closeInfoModal}
          >
            Ok, got it
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Payments;
